/**
 * Plan entitlements — which features this tenant's plan unlocks.
 *
 * The backend resolves the plan (ai.quota → ai.plans) and returns the shape on
 * `/me`. Locked features stay visible in the UI with a lock, so the tenant can
 * see what a higher plan adds.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getTenantMe } from "@/api/tenantDashboardApi";

/** Where an upgrade conversation goes. */
const CONTACT_BASE = "https://sentrodesk.com/contact/";

const EntitlementsContext = createContext(null);

/** Fallback while loading / if the call fails: unlock nothing extra. */
const EMPTY = {
  plan: "essential",
  planLabel: "Essential",
  features: [],
  entitledPageKeys: [],
  lockedFeatures: {},
  pageFeature: {},
};

export function EntitlementsProvider({ children }) {
  const [entitlements, setEntitlements] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getTenantMe();
        if (!cancelled && data?.entitlements) setEntitlements(data.entitlements);
      } catch {
        /* keep EMPTY — the UI degrades to "locked" rather than leaking access */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const features = new Set(entitlements.features || []);
    const pageFeature = entitlements.pageFeature || {};
    const locked = entitlements.lockedFeatures || {};

    /** Is a named feature unlocked on this plan? */
    const hasFeature = (key) => !key || features.has(key);

    /** Which feature (if any) gates a dashboard page. */
    const featureForPage = (pageKey) => pageFeature[pageKey] || null;

    /** Is this page locked by plan (vs. by RBAC)? */
    const isPageLocked = (pageKey) => {
      const f = featureForPage(pageKey);
      return Boolean(f) && !features.has(f);
    };

    /** Copy + upgrade link for a locked feature. */
    const lockInfo = (featureKey) => {
      const meta = locked[featureKey];
      if (!meta) return null;
      const interest = meta.interest || "general";
      return {
        label: meta.label || featureKey,
        requiredPlan: meta.required_plan || meta.requiredPlan || "growth",
        requiredPlanLabel: (meta.required_plan || "growth").replace(/^\w/, (c) => c.toUpperCase()),
        contactUrl: `${CONTACT_BASE}?interest=${encodeURIComponent(interest)}`,
      };
    };

    return {
      ...entitlements,
      loading,
      hasFeature,
      featureForPage,
      isPageLocked,
      lockInfo,
    };
  }, [entitlements, loading]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) throw new Error("useEntitlements must be used inside EntitlementsProvider");
  return ctx;
}
